import React, { useEffect, useState } from 'react';
import { WarningIcon, InfoIcon } from '@chakra-ui/icons';
import { checkCampaignTimeStatusViaChainManager } from '../../chain/utils/campaignUtils';

interface CampaignTimeStatusProps {
  lixiBagAddress: string;
}

interface TimeStatus {
  currentTime: number;
  closingTime: number;
  timeRemaining: number;
  isExpired: boolean;
  closingTimeDate: Date;
  currentTimeDate: Date;
}

export const CampaignTimeStatus: React.FC<CampaignTimeStatusProps> = ({
  lixiBagAddress,
}) => {
  const [timeStatus, setTimeStatus] = useState<TimeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkTime = async () => {
      if (!lixiBagAddress) return;

      try {
        setLoading(true);
        setError(null);
        const status = await checkCampaignTimeStatusViaChainManager(
          lixiBagAddress,
        );
        setTimeStatus(status);
      } catch (err) {
        setError('Không thể kiểm tra thời gian campaign');
        console.error('Error checking campaign time:', err);
      } finally {
        setLoading(false);
      }
    };

    checkTime();

    // Check every minute
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, [lixiBagAddress]);

  if (loading) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#2D3748',
          borderRadius: '6px',
          marginBottom: '16px',
        }}
      >
        <p style={{ color: 'white', fontSize: '14px', margin: 0 }}>
          Đang kiểm tra thời gian campaign...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#DC2626',
          borderRadius: '6px',
          border: '1px solid #EF4444',
          marginBottom: '16px',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
        >
          <WarningIcon color="white" style={{ marginRight: '8px' }} />
          <p
            style={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              margin: 0,
            }}
          >
            Lỗi!
          </p>
        </div>
        <p style={{ color: 'white', fontSize: '14px', margin: 0 }}>{error}</p>
      </div>
    );
  }

  if (!timeStatus) return null;

  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('vi-VN');
  };

  const formatDuration = (seconds: number) => {
    if (seconds <= 0) {
      return '0s';
    }

    const days = Math.floor(seconds / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}day${days > 1 ? 's' : ''}`);
    }
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (secs > 0 || parts.length === 0) {
      parts.push(`${secs}s`);
    }

    return parts.join(', ');
  };

  if (timeStatus.isExpired) {
    const timePassed = timeStatus.currentTime - timeStatus.closingTime;
    const daysPassed = Math.floor(timePassed / (24 * 3600));
    const hoursPassed = Math.floor((timePassed % (24 * 3600)) / 3600);
    const minutesPassed = Math.floor((timePassed % 3600) / 60);

    return (
      <div
        style={{
          padding: '16px',
          backgroundColor: '#B91C1C',
          borderRadius: '6px',
          border: '2px solid #EF4444',
          marginBottom: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <WarningIcon
            color="white"
            style={{ marginRight: '8px', fontSize: '20px' }}
          />
          <p
            style={{
              color: 'white',
              fontWeight: 'bold',
              fontSize: '16px',
              margin: 0,
            }}
          >
            Campaign đã hết hạn!
          </p>
        </div>
        <div style={{ color: 'white', fontSize: '14px', lineHeight: '1.6' }}>
          <p style={{ margin: '0 0 4px 0' }}>
            <span style={{ fontWeight: '600' }}>Thời gian đóng:</span>{' '}
            {formatTime(timeStatus.closingTime)}
          </p>
          <p style={{ margin: '0 0 4px 0' }}>
            <span style={{ fontWeight: '600' }}>Đã hết hạn:</span>{' '}
            {daysPassed > 0 ? `${daysPassed} ngày` : ''}
            {hoursPassed > 0 ? ` ${hoursPassed} giờ` : ''}
            {minutesPassed > 0 ? ` ${minutesPassed} phút` : ''} trước
          </p>
          <p style={{ margin: 0 }}>
            Vui lòng liên hệ admin để gia hạn hoặc tạo campaign mới.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '16px',
        backgroundColor: '#3B82F6',
        borderRadius: '6px',
        border: '2px solid #60A5FA',
        marginBottom: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}
      >
        <InfoIcon
          color="white"
          style={{ marginRight: '8px', fontSize: '20px' }}
        />
        <p
          style={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            margin: 0,
          }}
        >
          Thời gian campaign
        </p>
      </div>
      <div style={{ color: 'white', fontSize: '14px', lineHeight: '1.6' }}>
        <p style={{ margin: '0 0 4px 0' }}>
          <span style={{ fontWeight: '600' }}>Thời gian đóng:</span>{' '}
          {formatTime(timeStatus.closingTime)}
        </p>
        <p style={{ margin: 0 }}>
          <span style={{ fontWeight: '600' }}>Còn lại:</span>{' '}
          {formatDuration(timeStatus.timeRemaining)}
        </p>
      </div>
    </div>
  );
};
